import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bath, Utensils, Activity, HeartPulse, Home as HomeIcon, Clock, Stethoscope, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const SERVICES = [
  { icon: Bath, title: "Personal Care & Hygiene", desc: "Bathing, grooming, dressing and toileting assistance delivered with dignity and respect." },
  { icon: Utensils, title: "Meal Preparation & Nutrition", desc: "Nutritious meal planning and preparation tailored to dietary needs and preferences." },
  { icon: Activity, title: "Mobility & Fall Prevention", desc: "Safe transfers, walking support, and home safety to keep loved ones steady." },
  { icon: HeartPulse, title: "Respite Care", desc: "Short-term relief for family caregivers so they can rest and recharge." },
  { icon: Stethoscope, title: "Nursing Supervision", desc: "Licensed RN oversight, medication management, and wound care coordination." },
  { icon: HomeIcon, title: "Light Housekeeping", desc: "Laundry, tidying, and everyday tasks that keep the home comfortable and safe." },
];

export default function Services() {
  return (
    <div>
      <section className="bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <p className="text-xs uppercase tracking-[0.18em] text-amber-400 font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Ohio PASSPORT Approved</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold max-w-2xl leading-tight">In-home care services for Ohio families</h1>
          <p className="mt-5 text-blue-100/80 max-w-2xl text-lg leading-relaxed">Every service is delivered by vetted, trained caregivers under licensed nursing supervision — and may be covered through the Ohio Department of Aging PASSPORT / Medicaid Waiver program.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }}
              data-testid={`service-card-${i}`} className="bg-white dark:bg-blue-900/30 rounded-2xl p-7 border border-blue-900/5 shadow-sm hover:shadow-lg transition-shadow">
              <span className="grid place-items-center w-13 h-13 w-14 h-14 rounded-2xl bg-blue-100 text-blue-800 mb-5"><s.icon className="w-7 h-7" /></span>
              <h3 className="font-display text-xl font-semibold text-blue-950 dark:text-blue-50">{s.title}</h3>
              <p className="mt-2 text-slate-600 dark:text-slate-300 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-blue-100/60 dark:bg-blue-900/40 p-10 lg:p-14 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <Clock className="w-10 h-10 text-blue-800 dark:text-blue-300 mb-4" />
            <h2 className="font-display text-3xl font-semibold text-blue-950 dark:text-blue-50">Flexible scheduling, real people</h2>
            <p className="mt-3 text-slate-700 dark:text-slate-300 leading-relaxed">From a few hours a week to around-the-clock support, we build a care plan that fits your family. Speak with our Columbus care coordinators today.</p>
          </div>
          <div className="flex lg:justify-end gap-3">
            <Button asChild className="h-12 px-7 bg-blue-900 hover:bg-blue-950 text-white"><Link to="/request-care" data-testid="services-request-button">Request Care</Link></Button>
            <Button asChild variant="outline" className="h-12 px-7 border-blue-900/30 text-blue-900 dark:text-blue-100"><Link to="/marketplace">Meet Caregivers</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
